// functions to help merge settings and convert between settings flat list and objects
import {
  ConfigMetaData,
  ConfigOptionMetadata,
  ConfigurationDottedKey,
  keyToPath,
  pathToKey,
} from "./annotations";
import { getConfigurationMetaDataForSubObjectClass } from "./metadata";

// allowed values for leafs of configuration
export type AllowedValueType =
  | string
  | number
  | boolean
  | Array<string>
  | Array<number>
  | Array<boolean>
  | Record<string, string>
  | Record<string, number>
  | Record<string, boolean>;

export type ConfigurationSettingList = Record<
  ConfigurationDottedKey,
  AllowedValueType
>;

export type ValueWithSource = {
  source: string;
  value: AllowedValueType;
};
export type ConfigurationSettingListWithSource = Record<
  ConfigurationDottedKey,
  ValueWithSource
>;

export function toSettingsWithSource(
  source: string,
  settings: ConfigurationSettingList,
): ConfigurationSettingListWithSource {
  const entries = Object.entries(settings).map(([key, value]) => [
    key,
    { source, value },
  ]);
  return Object.fromEntries(entries);
}

export function toSettingsWithoutSource(
  settings: ConfigurationSettingListWithSource,
): ConfigurationSettingList {
  const entries = Object.entries(settings).map(([key, value]) => [
    key,
    value?.value,
  ]);
  return Object.fromEntries(entries);
}

export function mergeSettings(
  ...settings: ConfigurationSettingListWithSource[]
): ConfigurationSettingListWithSource {
  const result: ConfigurationSettingListWithSource = {};
  for (let setting of settings) {
    Object.assign(result, setting);
  }
  return result;
}

export function mergeSettingsWhichDiffer(
  ...settings: ConfigurationSettingListWithSource[]
): ConfigurationSettingListWithSource {
  const result: ConfigurationSettingListWithSource = {};
  const equals = (a, b) => a === b || a?.toString() == b?.toString();

  // we want to reverse iterate this list

  for (let setting of [...settings].reverse()) {
    for (let [key, value] of Object.entries(setting)) {
      if (!equals(result[key]?.value, value?.value)) result[key] = value;
    }
  }
  return result;
}
function filterEntries<V, MV = V>(
  obj: Record<string, V>,
  filter: (entry: [string, V]) => boolean,
  map: (entry: [string, V]) => [string, MV],
): Record<string, MV> {
  const entries = Object.entries(obj);
  const filtered = entries.filter(filter);
  if (map) {
    return Object.fromEntries(filtered.map(map));
  } else {
    return Object.fromEntries(filtered) as any;
  }
}

type NamedConfigurationSetting =
  | ConfigurationSettingListWithSource
  | [string, ConfigurationSettingList];

export class MergedConfigurationSettings {
  public readonly merged: ConfigurationSettingListWithSource = {};

  private readonly defaults: NamedConfigurationSetting[] = [];
  private readonly current: ConfigurationSettingList;

  /**
   *
   * @param current an object containing dottedKeys and simple values
   * @param defaults a list of one or more default sources. To provide a name for a source, use the
   * syntax [name, default-object]
   */
  constructor(
    current: ConfigurationSettingList,
    ...defaults: NamedConfigurationSetting[]
  ) {
    this.defaults = defaults;
    this.current = current;

    const listWithSource =
      defaults?.map((d) => {
        // convert everything to values with sources
        return Array.isArray(d) ? toSettingsWithSource(d[0], d[1]) : d;
      }) || [];
    listWithSource.unshift(toSettingsWithSource(null, current));
    this.merged = mergeSettingsWhichDiffer(...listWithSource);
  }

  /**
   * returns flat list of current settings which differ from default, this is what should be persisted
   */
  getNonDefaultSettings: () => ConfigurationSettingList = () => {
    return filterEntries(
      this.merged,
      ([k, v]) => !v.source,
      ([k, v]) => [k, v?.value],
    );
  };

  getSettingsForSubObject: (string) => ConfigurationSettingList = (
    rootKey: string = "",
  ) => {
    rootKey ||= "";
    const settings = trimSettingsToSubPath(rootKey, this.merged);
    return toSettingsWithoutSource(settings);
  };

  makeConfigObjectForKey: <T>(clazz: new () => T, key: string) => T = <T>(
    clazz: new () => T,
    key: string,
  ) => {
    const settings = this.getSettingsForSubObject(key);
    return makeConfigObjectFromSettings(clazz, settings);
  };

  /**
   * get current value for a dotted-key
   * if source of value is current-scope (non-default), the source will be NULL,
   * otherwise the source indicates from which default this comes
   * @param dottetdKey a key in dotted form
   */
  getValueForKey: (dottetdKey: string) => ValueWithSource = (
    dottetdKey: string,
  ) => {
    return this.merged[dottetdKey];
  };

  /**
   * get current value for an option described by meta-data
   * the return value will not neccessarily be the same as getValueForKey(option.key) because
   * this function takes into account object-level defaults which are published in the metadata
   * @param option a metadata object received from the metadata service
   */
  getValueForMetaDataOption: (option: ConfigOptionMetadata) => ValueWithSource =
    (option: ConfigOptionMetadata) => {
      return (
        this.getValueForKey(option.key) || {
          source: "default",
          value: option.defaultValue,
        }
      );
    };

  /**
   * returns a copy of the object with the mutated setting
   * @param dottedKey
   * @param value
   */
  withValue: (
    dottedKey: string,
    value: AllowedValueType,
  ) => MergedConfigurationSettings = (
    dottedKey: string,
    value: AllowedValueType,
  ) => {
    return new MergedConfigurationSettings(
      { ...this.current, [dottedKey]: value },
      ...this.defaults,
    );
  };
}

function trimSettingsToSubPath<
  S extends ConfigurationSettingList | ConfigurationSettingListWithSource,
>(rootKey: string, settings: S): S {
  rootKey ||= "";
  const rootPathLength = keyToPath(rootKey)?.length;
  const trimToRoot = (key: string) =>
    pathToKey(keyToPath(key).slice(rootPathLength));

  return filterEntries<AllowedValueType | ValueWithSource>(
    settings,
    ([k]) => k?.startsWith(rootKey),
    ([k, v]) => [trimToRoot(k), v],
  ) as S;
}

export function makeConfigObjectFromSettings<T>(
  clazz: new () => T,
  settings: ConfigurationSettingList,
): T {
  // create the object
  const configObj = new clazz();

  // apply root settings
  for (let [key, value] of Object.entries(settings)) {
    if (!key.includes(".")) {
      configObj[key] = value;
    }
  }
  // create subobjects
  const subobjects = getConfigurationMetaDataForSubObjectClass(clazz).filter(
    ConfigMetaData.isSubObject,
  );
  for (let so of subobjects) {
    if (so.isObjectMap) {
      const subsettings = trimSettingsToSubPath(so.key, settings);
      const roots = new Set(Object.keys(subsettings).map(k=>keyToPath(k)[0]));

      [...roots].filter(r=>{
        const unwrapped = r.replace(/\[|\]/g,"");
        configObj[so.key] ||= {};
        configObj[so.key][unwrapped] = makeConfigObjectFromSettings(
            so.objectClass,
            trimSettingsToSubPath(so.key + `.${r}`, settings),
        );
      })
    }
    else {
      configObj[so.key] = makeConfigObjectFromSettings(
          so.objectClass,
          trimSettingsToSubPath(so.key, settings),
      );
    }
  }
  return configObj;
}
